// 스탯 배분 칸(레인 C, docs/PC-WINDOWS-PRD.md 3.2): 가방 「상태」 탭 맨 위. 레벨업 포인트를 힘·민첩·지능·체력에 나눈다.
// 순수 모델(statPanelModel)과 DOM 그리기(drawStatPanel)를 나눴다. 그리기는 값이 바뀔 때만 rpg.ts가 부른다(프레임마다 아님).
import { STAT_IDS, STAT_TEXT, statRows as statAllocRows, statStep, type StatAlloc, type StatId, type StatRow, type StatView } from '../shared/stats';
import type { GameCommand } from '../shared/types';
import { FONT, el } from './dom';
import { makeRequestId } from './rpgModel';

export interface StatPanelRow extends StatRow { readonly canDec: boolean; readonly canInc: boolean }
export interface StatPanelModel {
  readonly usable: boolean;
  readonly free: number;
  readonly points: number;
  readonly rows: StatPanelRow[];
  /** 안내 문구(쓸 수 없을 때·포인트 0일 때). 없으면 '' */
  readonly hint: string;
  readonly canReset: boolean;
  /** 마지막 명령 결과 문구(ok·중복이면 '') */
  readonly message: string;
}

const NO_ALLOC: StatAlloc = { str: 0, dex: 0, int: 0, vit: 0 };

/** 뷰 → 표시 모델 (순수) */
export function statPanelModel(sv: StatView | undefined): StatPanelModel {
  const usable = !!sv && sv.mode === 'ok';
  const alloc = sv ? sv.alloc : NO_ALLOC;
  const points = sv ? sv.points : 0;
  const rows = statAllocRows(alloc).map((r) => ({
    ...r,
    canDec: usable && alloc[r.id] > 0,
    canInc: usable && statStep(alloc, r.id, 1, points) !== null,
  }));
  let hint = '';
  if (!sv) hint = '스탯 정보를 불러오는 중이에요.';
  else if (sv.mode !== 'ok') hint = STAT_TEXT.readonly;
  else if (points <= 0) hint = '레벨이 오르면 스탯 포인트(레벨당 2점)를 받아요.';
  const code = sv ? sv.last.code : 'ok';
  const message = sv && code !== 'ok' && code !== 'duplicate' ? (sv.last.message || STAT_TEXT[code]) : '';
  return { usable, free: sv ? sv.free : 0, points, rows, hint, canReset: usable && sv !== undefined && sv.free !== sv.points, message };
}

/** 한 칸을 ±1 한 새 배분(안 되면 null) — 단추 동작과 시험이 같이 쓴다 */
export function nextAlloc(sv: StatView, id: StatId, delta: 1 | -1): StatAlloc | null {
  return statStep(sv.alloc, id, delta, sv.points);
}

const BOX = 'display:flex;flex-direction:column;gap:6px;grid-column:1/-1;margin-bottom:6px;';
const ROW = 'display:flex;align-items:center;gap:8px;min-height:56px;padding:6px 10px;border-radius:10px;border:1.5px solid rgba(242,193,78,.4);background:linear-gradient(180deg,rgba(32,43,78,.95),rgba(12,17,34,.95));';
// 가방 창은 화면에 맞춰 zoom이 걸린다 — rpg.ts가 터치에서 확대 뒤에도 44px이 되게 --dh-btn-min을 준다
const BTN = 'flex:0 0 auto;min-width:var(--dh-btn-min,44px);min-height:var(--dh-btn-min,44px);border-radius:8px;border:1.5px solid rgba(242,193,78,.7);background:linear-gradient(#24305a,#151d38);color:#ffe07a;font:900 20px ' + FONT + ';cursor:pointer;';

export function drawStatPanel(parent: HTMLElement, sv: StatView | undefined, send: (c: GameCommand) => void): void {
  const m = statPanelModel(sv);
  const box = el('div', BOX, parent);
  box.dataset.testid = 'stat-panel';
  const head = el('div', 'display:flex;align-items:baseline;gap:8px;color:#ffe07a;font:900 15px ' + FONT + ';', box);
  el('span', '', head, '스탯 포인트');
  const free = el('span', 'margin-left:auto;color:#fff;', head, '남음 ' + m.free + ' / ' + m.points);
  free.dataset.testid = 'stat-free';
  free.setAttribute('aria-label', '스탯 포인트 남음 ' + m.free + ' / ' + m.points);
  // 다시 그리기 전에 빠르게 여러 번 눌러도 한 번씩 반영되게, 보낸 배분을 다음 클릭의 기준으로 쓴다(명령은 다음 틱에 처리된다)
  let cur: StatAlloc | null = sv ? { ...sv.alloc } : null;
  const go = (alloc: StatAlloc | null): void => { if (alloc && m.usable) { cur = alloc; send({ type: 'statAlloc', alloc, requestId: makeRequestId() }); } };
  const step = (id: StatId, d: 1 | -1): StatAlloc | null => (sv && cur ? statStep(cur, id, d, sv.points) : null);
  for (const r of m.rows) {
    const line = el('div', ROW, box);
    line.dataset.testid = 'stat-' + r.id;
    const copy = el('div', 'flex:1;min-width:0;', line);
    el('b', 'display:block;font:800 15px ' + FONT + ';color:#f1f3f9;', copy, r.name + ' ' + r.short + ' · ' + r.effect);
    el('small', 'display:block;font:600 12px ' + FONT + ';color:#abbcd3;', copy, r.per);
    const dec = el('button', BTN, line, '−'); dec.type = 'button'; dec.dataset.testid = 'stat-' + r.id + '-dec';
    dec.disabled = !r.canDec; dec.setAttribute('aria-label', r.name + ' 포인트 빼기');
    if (!r.canDec) dec.style.opacity = '.4';
    dec.addEventListener('click', () => { go(step(r.id, -1)); });
    const val = el('span', 'min-width:30px;text-align:center;font:900 18px ' + FONT + ';color:#ffe07a;', line, String(r.value));
    val.dataset.testid = 'stat-' + r.id + '-value';
    const inc = el('button', BTN, line, '+'); inc.type = 'button'; inc.dataset.testid = 'stat-' + r.id + '-inc';
    inc.disabled = !r.canInc; inc.setAttribute('aria-label', r.name + ' 포인트 더하기');
    if (!r.canInc) inc.style.opacity = '.4';
    inc.addEventListener('click', () => { go(step(r.id, 1)); });
  }
  const reset = el('button', BTN + 'width:100%;font-size:14px;', box, '스탯 초기화 (무료)'); reset.type = 'button';
  reset.dataset.testid = 'stat-reset';
  reset.disabled = !m.canReset;
  if (!m.canReset) reset.style.opacity = '.4';
  reset.addEventListener('click', () => { go({ str: 0, dex: 0, int: 0, vit: 0 }); });
  if (m.hint) el('p', 'margin:0;color:#bdcce0;font:600 12px ' + FONT + ';line-height:1.5;', box, m.hint);
  if (m.message) el('p', 'margin:0;color:#ffd0ac;font:700 12px ' + FONT + ';', box, m.message).setAttribute('role', 'status');
}

/** 다시 그리기 키: 배분·결과가 바뀔 때만 값이 달라진다 */
export function statPanelKey(sv: StatView | undefined): number { return sv ? sv.revision * 1000 + (sv.last.seq % 1000) : -1; }
